import { XHRRequestCanceler } from '../../Libs/xhr/XHRRequestCanceler';
import {
    AddOrEditAffaireDetails,
    AffaireControlApiDataResponse,
    AffaireControlParams,
    AffaireDetails,
    AffaireDetailsApiDataResponse,
    AffaireListOrder,
    AffaireListResponse,
} from '../Models/Affaires/Affaires';
import { Element, ElementListResponse, UsageByWeekResponse } from '../Models/Affaires/Elements';
import { BaseApiDataResponse } from '../Models/ApiData';
import { BaseDataService } from './Base/BaseDataService';

export const AffairesEndpoints = {
    AFFAIRES: '/api/v1/back/affaires',
    EXPORT_AFFAIRE_LIST: '/api/v1/back/affaires/export',
    SET_AFFAIRE_STATE: (affaire_id: string) => `/api/v1/back/affaires/${affaire_id}/is_active`,
    AFFAIRES_CONTROLS: 'api/v1/back/checks',
    AFFAIRE_DETAILS: (affaire_id: string) => `api/v1/back/affaires/${affaire_id}`,
    PATCH_AFFAIRE: (affaireId: string) => `/api/v1/back/affaires/${affaireId}`,
    CREATE_AFFAIRE: '/api/v1/back/affaires',
    ELEMENTS: (affaire_id: string) => `api/v1/back/affaires/${affaire_id}/elements`,
    ELEMENT_USAGE: 'api/v1/back/elements/usage',
};

export interface IAffairesDataService {
    getAffaireList: (
        cts: XHRRequestCanceler,
        search: string,
        isActive: boolean,
        order?: AffaireListOrder
    ) => Promise<AffaireListResponse>;

    setAffaireState: (
        cts: XHRRequestCanceler,
        affaireId: string,
        state: boolean
    ) => Promise<BaseApiDataResponse>;

    getAffaireControl: (
        params: AffaireControlParams,
        cts: XHRRequestCanceler
    ) => Promise<AffaireControlApiDataResponse>;

    getAffaireDetails: (
        affaireId: string,
        cts: XHRRequestCanceler
    ) => Promise<AffaireDetailsApiDataResponse>;

    patchAffaire: (
        affaireId: string,
        affaireInfos: AddOrEditAffaireDetails,
        cts: XHRRequestCanceler
    ) => Promise<BaseApiDataResponse>;

    createAffaire: (
        affaireInfos: AddOrEditAffaireDetails,
        cts: XHRRequestCanceler
    ) => Promise<AffaireDetailsApiDataResponse>;

    listElements: (cts: XHRRequestCanceler, affaireId: string) => Promise<ElementListResponse>;

    setElements: (
        cts: XHRRequestCanceler,
        affaireId: string,
        elements: Element[]
    ) => Promise<BaseApiDataResponse>;

    createElement: (
        cts: XHRRequestCanceler,
        affaireId: string,
        element: Element
    ) => Promise<BaseApiDataResponse>;

    getUsageByWeek: (cts: XHRRequestCanceler, element: Element) => Promise<UsageByWeekResponse>;
}

class AffairesDataService extends BaseDataService implements IAffairesDataService {
    public getUsageByWeek = async (
        cts: XHRRequestCanceler,
        element: Element
    ): Promise<UsageByWeekResponse> => {
        return await this.post<UsageByWeekResponse>(
            AffairesEndpoints.ELEMENT_USAGE,
            { ...element },
            cts
        );
    };

    public setElements = async (
        cts: XHRRequestCanceler,
        affaireId: string,
        elements: Element[]
    ): Promise<BaseApiDataResponse> => {
        return await this.put(
            AffairesEndpoints.ELEMENTS(affaireId),
            {
                elements,
            },
            cts
        );
    };

    public createElement = async (
        cts: XHRRequestCanceler,
        affaireId: string,
        element: Element
    ): Promise<BaseApiDataResponse> => {
        return await this.post(
            AffairesEndpoints.ELEMENTS(affaireId),
            {
                ...element,
            },
            cts
        );
    };

    public listElements = async (
        cts: XHRRequestCanceler,
        affaireId: string
    ): Promise<ElementListResponse> => {
        return await this.get<ElementListResponse>(AffairesEndpoints.ELEMENTS(affaireId), cts);
    };

    public getAffaireList = (
        cts: XHRRequestCanceler,
        search: string,
        isActive: boolean,
        order?: AffaireListOrder
    ) => {
        const params = {
            search: search,
            is_active: isActive ? true : null,
            order: order,
        };
        return this.get<AffaireListResponse>(AffairesEndpoints.AFFAIRES, cts, { params });
    };

    public setAffaireState = (cts: XHRRequestCanceler, affaireId: string, state: boolean) => {
        return this.patch(
            AffairesEndpoints.SET_AFFAIRE_STATE(affaireId),
            { is_active: state },
            cts
        );
    };

    public getAffaireControl = (params: AffaireControlParams, cts: XHRRequestCanceler) => {
        return this.get<AffaireControlApiDataResponse>(AffairesEndpoints.AFFAIRES_CONTROLS, cts, {
            params,
        });
    };

    public getAffaireDetails = (affaireId: string, cts: XHRRequestCanceler) => {
        return this.get<AffaireDetailsApiDataResponse>(
            AffairesEndpoints.AFFAIRE_DETAILS(affaireId),
            cts
        );
    };

    public patchAffaire = (
        affaireId: string,
        affaireInfos: AddOrEditAffaireDetails,
        cts: XHRRequestCanceler
    ) => {
        return this.patch<BaseApiDataResponse>(
            AffairesEndpoints.PATCH_AFFAIRE(affaireId),
            affaireInfos,
            cts
        );
    };

    public createAffaire = (
        affaireInfos: AddOrEditAffaireDetails,
        cts: XHRRequestCanceler
    ) => {
        return this.post<AffaireDetailsApiDataResponse>(
            AffairesEndpoints.CREATE_AFFAIRE,
            affaireInfos,
            cts,
            {
                checkError: false,
            }
        );
    };
}

const AffairesDataServiceName = 'AffairesDataService';

export { AffairesDataService, AffairesDataServiceName };
